// Apolo — Self-Repair state machine (DOMAIN, pure). "Execution repairs itself first."
//
// A PURE reducer: given the machine context + an event, return the NEXT context plus
// the `action` the driver should run. ZERO framework imports, ZERO side effects — it
// only DECIDES. This is what makes the loop bounded and the architecture clean: the
// driver, checker and UI all depend INWARD on this; this depends on nothing.
//
// Maps the canonical agent loop's "reflect/repair" stage made deterministic:
//   CHECKING(CHECKER) → FIXING(FIXER) → DOUBLECHECKING(DOUBLECHECKER) →
//   TROUBLESHOOTING(TROUBLESHOOTER) → ESCALATED(CONSULTANT), bounded by BLOCKED_THRESHOLD.
//
// Note: the image's FIX_NEEDED / FIXER_COMPLETE / DOUBLECHECK_PASS labels are EVENTS
// (signals), not states. Here a failed check is the `CHECK_RESULT {passed:false}` event;
// the fixer-running state is FIXING.

export const STATES = Object.freeze({
  PROPOSED: 'PROPOSED', // nothing run yet
  CHECKING: 'CHECKING', // running the verify command (CHECKER)
  FIXING: 'FIXING', // a fixer sub-run is repairing (FIXER)
  DOUBLECHECKING: 'DOUBLECHECKING', // re-running verify after a repair (DOUBLECHECKER)
  TROUBLESHOOTING: 'TROUBLESHOOTING', // a deeper systematic-debugging sub-run (TROUBLESHOOTER)
  PASS: 'PASS', // terminal: verify passed (ASSURANCE_SIGNAL ✓)
  ESCALATED: 'ESCALATED', // terminal: exhausted/aborted → needs planning (CONSULTANT)
});

export const DEFAULT_BLOCKED_THRESHOLD = 2;

const TERMINAL = new Set([STATES.PASS, STATES.ESCALATED]);

export function isTerminal(state) {
  return TERMINAL.has(state);
}

/** Fresh machine context. */
export function initialRepair({ blockedThreshold = DEFAULT_BLOCKED_THRESHOLD } = {}) {
  const t = Number.isFinite(blockedThreshold) && blockedThreshold > 0
    ? Math.floor(blockedThreshold)
    : DEFAULT_BLOCKED_THRESHOLD;
  return { state: STATES.PROPOSED, cycles: 0, blockedThreshold: t };
}

// Build a fresh result object (never mutates `ctx`).
function step(ctx, { state, cycles, action = null }) {
  return {
    state,
    cycles: cycles ?? ctx.cycles,
    blockedThreshold: ctx.blockedThreshold,
    action,
    terminal: TERMINAL.has(state),
    escalated: state === STATES.ESCALATED,
  };
}

/**
 * Pure transition. `event` = { type } where type is one of:
 *   'START'                              — begin (PROPOSED → CHECKING, run_check)
 *   'CHECK_RESULT', passed:boolean       — a verify result (in CHECKING / DOUBLECHECKING)
 *   'AGENT_DONE', ok?:boolean            — a fixer/troubleshooter sub-run finished
 *   'ABORT'                              — user/system abort → ESCALATED (from any non-terminal)
 *
 * Returns { state, cycles, blockedThreshold, action, terminal, escalated }.
 * `action` ∈ 'run_check' | 'run_fixer' | 'run_troubleshooter' | null — what the driver runs next.
 * Unknown (event, state) pairs are a safe no-op (stay in the current state, action null).
 */
export function transition(ctx, event) {
  const type = event && event.type;

  // ABORT short-circuits from any non-terminal state.
  if (type === 'ABORT') {
    return isTerminal(ctx.state) ? step(ctx, { state: ctx.state }) : step(ctx, { state: STATES.ESCALATED });
  }

  switch (ctx.state) {
    case STATES.PROPOSED:
      if (type === 'START') return step(ctx, { state: STATES.CHECKING, action: 'run_check' });
      break;

    case STATES.CHECKING:
      if (type === 'CHECK_RESULT') {
        return event.passed
          ? step(ctx, { state: STATES.PASS })
          : step(ctx, { state: STATES.FIXING, action: 'run_fixer' });
      }
      break;

    case STATES.FIXING:
      if (type === 'AGENT_DONE') return step(ctx, { state: STATES.DOUBLECHECKING, action: 'run_check' });
      break;

    case STATES.DOUBLECHECKING:
      if (type === 'CHECK_RESULT') {
        if (event.passed) return step(ctx, { state: STATES.PASS });
        // A repaired-then-still-failing round = one blocked cycle.
        const cycles = ctx.cycles + 1;
        if (cycles >= ctx.blockedThreshold) {
          return step(ctx, { state: STATES.ESCALATED, cycles });
        }
        return step(ctx, { state: STATES.TROUBLESHOOTING, cycles, action: 'run_troubleshooter' });
      }
      break;

    case STATES.TROUBLESHOOTING:
      if (type === 'AGENT_DONE') return step(ctx, { state: STATES.DOUBLECHECKING, action: 'run_check' });
      break;

    default:
      break;
  }

  // No-op: stay put, run nothing.
  return step(ctx, { state: ctx.state });
}
